import React, { lazy, useEffect } from 'react';
import PropTypes from 'prop-types';

import container from 'containers/HomeContainer';
import GolfSpecialsBanner from 'pages/home/GolfSpecialsBanner';
import GolfClubCourses from 'pages/home/GolfClubCourses';
import GuestDisclaimer from 'pages/home/GuestDisclaimer';
import GetTheApp from 'pages/home/GetTheApp';
import OurAffiliates from 'pages/home/OurAffiliates';
import ForCourseBanner from 'pages/home/ForCourseBanner';
import HereToHelp from 'pages/home/HereToHelp';
import Search from 'components/search/Search';

import { componentLoader } from 'utils/componentLoader';
import MetaTagsHelper from 'utils/metaTagsHelper';
import { features } from 'utils/features';

const FeaturedBanner = lazy(() => componentLoader(() => import(/* webpackChunkName: "FeaturedBanner" */ 'pages/home/FeaturedBanner')));
const CoursesNearYou = lazy(() => componentLoader(() => import(/* webpackChunkName: "CoursesNearYou" */ 'pages/home/coursesNearYou/CoursesNearYou')));

const Home = ({
  contests,
  golfSpecials,
  subscribe,
  success,
  networkMembership,
  trackGAEvent,
  preloadComponent,
}) => {
  const tagsHelmet = MetaTagsHelper.buildHelmetTags({
    title: `${process.env.REACT_APP_META_TITLE} | Discount Tee Times, Deals & Course Reviews`,
    keywords: `tee times, golf course near me, golf courses, golf tee times, discounted tee times, golf deals, tee time deals, tee times near me, book tee times, ${process.env.REACT_APP_FRIENDLY_NAME}`,
    titleTemplate: '%s',
  });

  useEffect(() => {
    if (preloadComponent) preloadComponent();
  }, []);

  return (
    <>
      {tagsHelmet}
      <Search
        wrapperStyles="pt-8 px-4 flex justify-center md:px-10 md:pt-16 lg:px-14 2xl:px-0"
        initialAtCurrentLocation
        withMobileForm
        withHeader
        isHomePage
        isSticky
      />
      <div className="secondary-wrapper">
        {features.featuredContests && (
          <FeaturedBanner
            featuredContests={contests}
            className="my-12 flex justify-center md:my-16 md:px-10 lg:px-14"
          />
        )}

        {features?.golfSpecials && golfSpecials && (
          <GolfSpecialsBanner
            className="my-12 px-4 flex justify-center md:my-16 md:px-10 lg:px-14"
            golfSpecials={golfSpecials}
            subscribe={subscribe}
            success={success}
            trackGAEvent={trackGAEvent}
          />
        )}

        {(features.nearMeNav || features.recommendedCourses) && (
          <CoursesNearYou className="relative my-12 flex justify-center md:px-10 lg:my-16 lg:px-14" />
        )}
        {features.networkMembership && networkMembership && <GolfClubCourses />}

        <GuestDisclaimer className="cursor-pointer my-12 px-4 flex justify-center md:my-16 md:px-10 lg:px-14" />

        {(process.env.REACT_APP_IOS_APP_LINK || process.env.REACT_APP_GOOGLE_APP_LINK) && (
          <GetTheApp className="my-12 px-4 flex justify-center md:px-10 lg:my-16 lg:px-14" />
        )}

        {features.hereToHelp && (
          <HereToHelp className="my-12 px-4 flex justify-center md:px-10 lg:my-16 lg:px-14" />
        )}

        {process.env.REACT_APP_NAME === 'supremeGolf' && (
          <OurAffiliates className="my-12 px-4 flex justify-center md:px-10 lg:my-16 lg:px-14" />
        )}

        {features.forCourses && (
          <ForCourseBanner className="my-12 px-4 flex justify-center md:px-10 lg:my-16 lg:px-14" />
        )}
      </div>
    </>
  );
};

Home.defaultProps = {
  contests: [],
  golfSpecials: null,
  networkMembership: null,
  success: false,
  trackGAEvent: null,
  preloadComponent: null,
};

Home.propTypes = {
  contests: PropTypes.arrayOf(PropTypes.object),
  golfSpecials: PropTypes.shape({}),
  networkMembership: PropTypes.shape({}),
  subscribe: PropTypes.func.isRequired,
  trackGAEvent: PropTypes.func,
  success: PropTypes.bool,
  preloadComponent: PropTypes.func,
};

export default container(Home);
